/**
 * PAEBM — edificações ZAS + pontos de encontro (dado do mapa M2). GUPM-195.
 * TS puro: tipos de domínio + tipos de linha (DB) + mappers DB→domínio + cores.
 *
 * Modelo V8 "edificações SEM moradores fixos" → SEM PII (resolve a barreira LGPD
 * que existia no `features/M2-mapa/domain/paebm.ts`). Fonte canônica: PAEBM BB
 * Rev.2/Abr 2026 (WA02821254-1-HL-RTE-0002), Seções 8 (pontos de encontro) e 10
 * (edificações). Coordenadas já em lat/lon SIRGAS2000 (sem conversão UTM).
 */

// ── Domínio (camelCase, consumido pela UI) ───────────────────────────────────

export interface ZasEdificacao {
  codigo: string;
  label: string;
  lat: number;
  lon: number;
  /** Ponto de encontro de destino (ex: 'PE-04'). */
  pontoEncontro: string | null;
  /** Sempre 0 no modelo V8 (sem moradores fixos). */
  residentes: number;
  sinore: boolean;
  distancia: string | null;
  nota: string | null;
  ne3: boolean;
}

export type PontoEncontroTipo = 'interno' | 'externo';

export interface PontoEncontro {
  codigo: string;
  label: string;
  tipo: PontoEncontroTipo;
  descricao: string | null;
  lat: number;
  lon: number;
  populacaoEstimada: number | null;
  capacidade: string | null;
  tempoRota: string | null;
  nivel: string | null;
  acesso: string | null;
}

/**
 * Cor do marcador de PE por tipo — paleta de MARCADOR do V8 (map legend
 * 5040-5041), não a do painel-lista. Interno = azul, externo = laranja.
 */
export const PONTO_ENCONTRO_COLOR: Record<PontoEncontroTipo, string> = {
  interno: '#2563eb',
  externo: '#ea580c',
};

// ── Linhas cruas (snake_case, como vêm do Postgres) ───────────────────────────

export interface ZasEdificacaoRow {
  codigo: string;
  label: string;
  lat: number;
  lon: number;
  ponto_encontro: string | null;
  residentes: number;
  sinore: boolean;
  distancia: string | null;
  nota: string | null;
  ne3: boolean;
}

export interface PontoEncontroRow {
  codigo: string;
  label: string;
  tipo: string;
  descricao: string | null;
  lat: number;
  lon: number;
  populacao_estimada: number | null;
  capacidade: string | null;
  tempo_rota: string | null;
  nivel: string | null;
  acesso: string | null;
}

// ── Mappers DB → domínio ──────────────────────────────────────────────────────

/**
 * Projeta a linha para o domínio. Reprojeção EXPLÍCITA dos campos permitidos:
 * se a linha trouxer uma coluna inesperada (ex: PII), ela NÃO vaza para o cliente
 * (defesa por projeção — barreira LGPD).
 */
export function mapEdificacaoRow(row: ZasEdificacaoRow): ZasEdificacao {
  return {
    codigo: row.codigo,
    label: row.label,
    lat: row.lat,
    lon: row.lon,
    pontoEncontro: row.ponto_encontro,
    residentes: row.residentes,
    sinore: row.sinore,
    distancia: row.distancia,
    nota: row.nota,
    ne3: row.ne3,
  };
}

export function mapPontoEncontroRow(row: PontoEncontroRow): PontoEncontro {
  return {
    codigo: row.codigo,
    label: row.label,
    // Narrow conservador: só 'interno' é interno; qualquer outra coisa → 'externo'
    // (não quebra o mapa se o banco trouxer um valor fora do esperado).
    tipo: row.tipo === 'interno' ? 'interno' : 'externo',
    descricao: row.descricao,
    lat: row.lat,
    lon: row.lon,
    populacaoEstimada: row.populacao_estimada,
    capacidade: row.capacidade,
    tempoRota: row.tempo_rota,
    nivel: row.nivel,
    acesso: row.acesso,
  };
}
